export interface IdleRequestCallback {
  (deadline: IdleDeadline): void;
}

export interface AsyncIdleRequestCallback {
  (deadline: IdleDeadline): Promise<void>;
}

export interface IdleDeadline {
  timeRemaining(): number;
  readonly didTimeout: boolean;
}

export interface IdleRequestOptions {
  timeout?: number;
}

export interface MayHaveRequestIdleCallback {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
}

/**
 * Request that a callback run when the main thread is idle.
 *
 * If the native requestIdleCallback() API is available, it will be used.
 * Otherwise, the callback will be invoked via setTimeout().
 */
export function requestIdleCallback(
  callback: IdleRequestCallback,
  options?: IdleRequestOptions,
): void {
  const wnd = window as Window & MayHaveRequestIdleCallback;
  if (wnd.requestIdleCallback) {
    wnd.requestIdleCallback(callback, options);
    return;
  }
  setTimeout(() => {
    callback({
      timeRemaining() {
        return 16;
      }, // One frame at 60fps.
      didTimeout: true,
    });
  }, options?.timeout ?? 16);
}

/**
 * A promise-based interface to requestIdleCallback().
 *
 * If the native requestIdleCallback() API is available, it will be used.
 * Otherwise, the callback will be invoked via setTimeout().
 *
 * @param options - Options for requestIdleCallback().
 * @returns a promise that resolves when the main thread is idle.
 */
export function untilIdle(options?: IdleRequestOptions): Promise<IdleDeadline> {
  return new Promise<IdleDeadline>(resolve => {
    requestIdleCallback((deadline: IdleDeadline) => {
      resolve(deadline);
    }, options);
  });
}
